/**
 * InboxInstanceNotice — Avisos de instância no modal "Nova inbox"
 *
 * Substitui os antigos <Alert> genéricos por cartões alinhados ao novo leiaute
 * do modal (barra de acento em gradiente, bloom decorativo, tile arredondado).
 *
 * Variantes:
 *   - 'required' : nenhuma instância disponível — cartão comercial com avatar de
 *                  especialista, selo premium, trilha de passos e CTA "Ver planos"
 *   - 'pending'  : instância solicitada aguardando liberação do administrador
 *   - 'auto'     : cota livre, instância será criada automaticamente ao salvar
 *
 * RELEASE:
 *   2026-08-08 v3.0.0 - Variantes 'pending' e 'auto' passam a usar o mesmo leiaute
 *                       split do bloco premium (sem avatar), com trilha de passos.
 *   2026-08-08 v2.0.0 - Variante 'required' redesenhada (direção "Especialista"):
 *                       layout split com avatar, badge premium e CTA comercial.
 *   2026-08-08 v1.0.0 - Versão inicial.
 */
import React from 'react';
import { Clock, ShoppingCart, ArrowRight, Sparkles, Check, BadgeCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { getChannelVisual } from './channelVisuals';
import specialistAvatar from '@/assets/inbox-specialist-avatar.jpg';

export type InboxInstanceNoticeVariant = 'required' | 'pending' | 'auto';

interface InboxInstanceNoticeProps {
  /** Variante do aviso */
  variant: InboxInstanceNoticeVariant;
  /** Canal selecionado (usado para cor/gradiente na variante 'required') */
  channelType?: string | null;
  /** Nome amigável do provedor/canal exibido no texto */
  providerName: string;
  /** Callback do CTA principal (apenas na variante 'required') */
  onViewPlans?: () => void;
}

export function InboxInstanceNotice({
  variant,
  channelType,
  providerName,
  onViewPlans,
}: InboxInstanceNoticeProps) {
  const channel = getChannelVisual(channelType);

  // ---------------------------------------------------------------------------
  // Variante comercial: cartão split com avatar de especialista
  // ---------------------------------------------------------------------------
  if (variant === 'required') {
    return (
      <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
        {/* Barra de acento superior na cor do canal */}
        <span
          aria-hidden="true"
          className={cn(
            'pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r',
            channel.accentGradient,
          )}
        />

        <div className="flex flex-col md:flex-row">
          {/* Coluna do especialista */}
          <div
            className={cn(
              'relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden border-b p-6 md:w-56 md:border-b-0 md:border-r',
              channel.bgColor,
            )}
          >
            <span
              aria-hidden="true"
              className={cn(
                'pointer-events-none absolute -top-10 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full blur-3xl',
                channel.bloomBg,
              )}
            />
            <div className="relative z-10">
              <div className="h-24 w-24 overflow-hidden rounded-full border-4 border-background shadow-md">
                <img
                  src={specialistAvatar}
                  alt="Especialista de atendimento"
                  loading="lazy"
                  width={512}
                  height={512}
                  className="h-full w-full object-cover"
                />
              </div>
              <span
                className={cn(
                  'absolute -bottom-1 -right-1 flex h-7 w-7 items-center justify-center rounded-lg bg-gradient-to-br shadow-md',
                  channel.accentGradient,
                )}
              >
                <Check className="h-4 w-4 text-primary-foreground" />
              </span>
            </div>
            <div className="relative z-10 text-center">
              <p className="text-sm font-semibold text-foreground">Especialista Uaizy</p>
              <p className="text-xs text-muted-foreground">Pronto para te ajudar</p>
            </div>
          </div>

          {/* Coluna de conteúdo e conversão */}
          <div className="flex flex-1 flex-col p-6">
            <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              <BadgeCheck className="h-3.5 w-3.5" />
              Recurso premium
            </span>

            <h4 className="text-lg font-bold leading-tight text-foreground">
              Contrate uma instância do {providerName}
            </h4>
            <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
              Contrate agora e comece a atender em minutos: nós criamos e conectamos a instância
              automaticamente para você — sem configuração técnica.
            </p>

            {/* Trilha de passos */}
            <div className="mt-6 flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-foreground text-xs font-bold text-background">
                  1
                </span>
                <span className="text-xs font-bold text-foreground">Contratar</span>
              </div>
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-2 opacity-50">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                  2
                </span>
                <span className="text-xs font-semibold text-muted-foreground">Criação automática</span>
              </div>
              <div className="h-px flex-1 bg-border" />
              <div className="flex items-center gap-2 opacity-50">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-muted text-xs font-bold text-muted-foreground">
                  3
                </span>
                <span className="text-xs font-semibold text-muted-foreground">Criar inbox</span>
              </div>
            </div>


            {/* CTA comercial */}
            <div className="mt-6 flex flex-col items-center gap-3 sm:flex-row">
              <Button
                onClick={onViewPlans}
                className={cn(
                  'group h-12 w-full rounded-xl bg-gradient-to-r px-8 text-sm font-bold text-primary-foreground shadow-lg hover:opacity-90 sm:w-auto',
                  channel.accentGradient,
                )}
              >
                Contratar instância

                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                <ShoppingCart className="h-3.5 w-3.5" />
                Ativação em poucos minutos
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Variantes informativas (pending / auto) — mesmo leiaute split, sem avatar
  // ---------------------------------------------------------------------------
  const palette =
    variant === 'pending'
      ? {
          gradient: 'from-amber-500 to-orange-400',
          bloom: 'bg-amber-500/25',
          soft: 'bg-amber-500/10',
          Icon: Clock,
          sideLabel: 'Aguardando liberação',
          badge: 'Em análise',
          title: 'Aguardando liberação',
          currentStep: 1,
          footer: 'Você será avisado assim que a conexão for liberada.',
        }
      : {
          gradient: 'from-emerald-500 to-teal-400',
          bloom: 'bg-emerald-500/25',
          soft: 'bg-emerald-500/10',
          Icon: Sparkles,
          sideLabel: 'Conexão automática',
          badge: 'Incluso no seu plano',
          title: 'Tudo pronto: conexão automática',
          currentStep: 2,
          footer: 'Nenhuma ação necessária — criamos a conexão ao salvar.',
        };

  const { Icon } = palette;

  return (
    <div className="relative overflow-hidden rounded-2xl border bg-card shadow-sm">
      {/* Barra de acento superior */}
      <span
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute inset-x-0 top-0 h-1 bg-gradient-to-r',
          palette.gradient,
        )}
      />

      <div className="flex flex-col md:flex-row">
        {/* Coluna lateral do ícone (substitui o avatar da variante comercial) */}
        <div
          className={cn(
            'relative flex w-full flex-col items-center justify-center gap-3 overflow-hidden border-b p-6 md:w-56 md:border-b-0 md:border-r',
            palette.soft,
          )}
        >
          <span
            aria-hidden="true"
            className={cn(
              'pointer-events-none absolute -top-10 left-1/2 h-32 w-32 -translate-x-1/2 rounded-full blur-3xl',
              palette.bloom,
            )}
          />
          <div
            className={cn(
              'relative z-10 flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br shadow-md',
              palette.gradient,
            )}
          >
            <Icon className="h-7 w-7 text-primary-foreground" />
          </div>
          <p className="relative z-10 text-center text-sm font-semibold text-foreground">
            {palette.sideLabel}
          </p>
        </div>

        {/* Coluna de conteúdo */}
        <div className="flex flex-1 flex-col p-6">
          <span className="mb-3 inline-flex w-fit items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            <BadgeCheck className="h-3.5 w-3.5" />
            {palette.badge}
          </span>

          <h4 className="text-lg font-bold leading-tight text-foreground">{palette.title}</h4>
          <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
            {variant === 'pending' ? (
              <>
                Sua solicitação de conexão <strong>{providerName}</strong> foi registrada. Assim que
                o administrador liberar, você poderá concluir a criação da inbox.
              </>
            ) : (
              <>
                Você ainda tem disponibilidade no seu plano. Ao criar a inbox, uma nova conexão{' '}
                <strong>{providerName}</strong> será criada e vinculada automaticamente.
              </>
            )}
          </p>

          {/* Trilha de passos (mesma da variante comercial) */}
          <StepTrail currentStep={palette.currentStep} gradient={palette.gradient} />

          <p className="mt-5 text-xs text-muted-foreground">{palette.footer}</p>
        </div>
      </div>
    </div>
  );
}

/**
 * StepTrail — trilha visual de 3 passos (Contratar / Criação automática / Criar inbox)
 *
 * @param currentStep Índice (0-based) do passo ativo; anteriores viram check.
 * @param gradient    Classes de gradiente usadas nos passos concluídos.
 */
function StepTrail({ currentStep, gradient }: { currentStep: number; gradient: string }) {
  const steps = ['Contratar', 'Criação automática', 'Criar inbox'];


  return (
    <div className="mt-6 flex flex-wrap items-center gap-3">
      {steps.map((label, index) => {
        const done = index < currentStep;
        const active = index === currentStep;

        return (
          <React.Fragment key={label}>
            {index > 0 && <div className="h-px min-w-4 flex-1 bg-border" />}
            <div className={cn('flex items-center gap-2', !done && !active && 'opacity-50')}>
              <span
                className={cn(
                  'flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold',
                  done && cn('bg-gradient-to-br text-primary-foreground', gradient),
                  active && 'bg-foreground text-background',
                  !done && !active && 'bg-muted text-muted-foreground',
                )}
              >
                {done ? <Check className="h-3.5 w-3.5" /> : index + 1}
              </span>
              <span
                className={cn(
                  'text-xs',
                  active ? 'font-bold text-foreground' : 'font-semibold text-muted-foreground',
                )}
              >
                {label}
              </span>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default InboxInstanceNotice;
